import { routeConfig } from "#web/configs/route";
import { i18nLike } from "#web/services/i18nLike";
import { Badge, Icon, IndexTable, InlineStack, Text, Tooltip } from "@shopify/polaris";
import { InfoIcon } from "@shopify/polaris-icons";
import { useNavigate } from "react-router-dom";

import { LeakedCode, leakedCodeEndedBeforeLeak, leakedCodeStartedAfterLeak } from "../_models/leakedCode";
import { LeakedCodes } from "../LeakedCodePageQuery";
import { LeakStatusBadge } from "../LeakStatusBadge";
import { ShopifyStatusBadge } from "../ShopifyStatusBadge";
import { HighRiskTooltip } from "./HighRiskTooltip";
import { SourceBadgeList } from "./SourceBadgeList";
import { en } from "./translations";
import { UsagePeriodsComparisonBadge } from "./UsagePeriodComparisonBadge";

interface LeakedCodesIndexTableRowProps {
  leakedCode: LeakedCodes["leakedCodes"][number];
  position: number;
  shopCurrency: string;
}
export const LeakedCodesIndexTableRow = ({ leakedCode, position, shopCurrency }: LeakedCodesIndexTableRowProps) => {
  const t = i18nLike(en);
  const navigate = useNavigate();

  const formatAverage = (average: number) => {
    if (isNaN(average)) {
      return "0";
    }

    if (average === 0) {
      return "0";
    }

    if (average < 1) {
      return "< 1";
    }

    if (average > 999) {
      return "999+";
    }

    return Math.round(average).toString();
  };

  const formatValue = (leakedCode: LeakedCode) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (leakedCode.value == null) {
      return "N/A";
    }

    return leakedCode.valueType === "percentage"
      ? `${String(Math.abs(leakedCode.value))}%`
      : `${String(Math.abs(leakedCode.value))} ${shopCurrency}`;
  };

  return (
    <IndexTable.Row
      id={leakedCode.id}
      onClick={() => {
        navigate(routeConfig.leaks[":id"]["/"].replace(":id", leakedCode.id));
      }}
      position={position}
    >
      {/* Code */}
      <IndexTable.Cell>
        {leakedCode.isHighRisk ? (
          <HighRiskTooltip leakedCode={leakedCode} />
        ) : (
          <Text as="span" fontWeight="bold" variant="bodyMd">
            {leakedCode.code}
          </Text>
        )}
      </IndexTable.Cell>

      {/* Leak status */}
      <IndexTable.Cell>
        <LeakStatusBadge leakedCode={leakedCode} />
      </IndexTable.Cell>

      {/* Shopify status */}
      <IndexTable.Cell>
        <ShopifyStatusBadge leakedCode={leakedCode} />
      </IndexTable.Cell>

      {/* Value */}
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd">
          {formatValue(leakedCode)}
        </Text>
      </IndexTable.Cell>

      {/* Avg weekly usage before leak */}
      <IndexTable.Cell>
        {leakedCodeStartedAfterLeak(leakedCode) ? (
          <Badge>{t("codeStartedAfterLeak")}</Badge>
        ) : (
          <Text as="span" variant="bodyMd">
            {formatAverage(leakedCode.averageWeeklyUsageBeforeLeak)} {t("avgUsageBeforeLeak.perWeek")}
          </Text>
        )}
      </IndexTable.Cell>

      {/* Avg weekly usage after leak */}
      <IndexTable.Cell>
        {leakedCode.leakedOnAppInstallation ? (
          <InlineStack gap={"100"}>
            <Text as="span" variant="bodyMd">
              {t("common.na")}
            </Text>
            <Tooltip content={t("leakedOnAppInstallation")}>
              <Icon source={InfoIcon} tone="subdued" />
            </Tooltip>
          </InlineStack>
        ) : leakedCodeEndedBeforeLeak(leakedCode) ? (
          <Badge>{t("codeEndedBeforeLeak")}</Badge>
        ) : (
          <InlineStack gap={"200"}>
            <Text as="span" variant="bodyMd">
              {formatAverage(leakedCode.averageWeeklyUsageAfterLeak)} {t("avgUsageAfterLeak.perWeek")}
            </Text>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
            {leakedCode.percentageDifference !== 0 && !leakedCode.leakedOnAppInstallation && (
              <UsagePeriodsComparisonBadge leakedCode={leakedCode} />
            )}
          </InlineStack>
        )}
      </IndexTable.Cell>

      {/* Sources */}
      <IndexTable.Cell>
        <SourceBadgeList leakedCode={leakedCode} />
      </IndexTable.Cell>

      {/* First seen */}
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd">
          {leakedCode.leakedAt.toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </Text>
      </IndexTable.Cell>
    </IndexTable.Row>
  );
};
